import React from 'react';
import pinkSrc from '../assets/key-tag-pink.svg';
import greenSrc from '../assets/key-tag-green.svg';

export type KeyTagVariant = 'pink' | 'green';
type KeyTagSize = 'xl' | 'md' | 'sm';

interface KeyTagProps {
  number?: number;
  courtNumber?: number; // alias
  variant?: KeyTagVariant;
  size?: KeyTagSize;
  hero?: boolean;  // treated same as size="xl"
  label?: string;
}

const SIZES: Record<KeyTagSize, { width: number; height: number }> = {
  xl: { width: 160, height: 245 },
  md: { width: 80,  height: 122 },
  sm: { width: 52,  height: 80  },
};

// SVG viewBox for the new vector assets: 0 0 1023 1537
// The baked-in number text is at x=511, y=1180, fontSize=222
const SVG_VB_W = 1023;
const SVG_VB_H = 1537;
const NUMBER_X = 511;
const NUMBER_Y = 1180;
const NUMBER_FONT_SIZE = 222;

export const KeyTag: React.FC<KeyTagProps> = ({
  number,
  courtNumber,
  variant = 'pink',
  size = 'md',
  hero = false,
  label,
}) => {
  const courtNum = number ?? courtNumber ?? 1;
  const effectiveSize: KeyTagSize = hero ? 'xl' : (size as KeyTagSize);
  const { width, height } = SIZES[effectiveSize] ?? SIZES['md'];
  const src = variant === 'pink' ? pinkSrc : greenSrc;
  const numStr = String(courtNum).padStart(2, '0');
  const ariaLabel = label ?? `Court ${courtNum} key tag`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ position: 'relative', width, height, flexShrink: 0 }}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      {/* Overlay: covers the baked-in "01" and renders the dynamic court number */}
      <svg
        viewBox={`0 0 ${SVG_VB_W} ${SVG_VB_H}`}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
        }}
      >
        {/* Opaque cover rect to erase baked-in number — positioned just behind our text */}
        <text
          x={NUMBER_X}
          y={NUMBER_Y}
          textAnchor="middle"
          dominantBaseline="auto"
          fontFamily="'Poppins', Arial, sans-serif"
          fontWeight="700"
          fontSize={NUMBER_FONT_SIZE}
          fill="#f8ebd2"
        >
          {numStr}
        </text>
      </svg>
    </div>
  );
};

export default KeyTag;
